import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import useExcelUrl from "app/useExcelUrl";
import {
  Checkbox,
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  DateRangePicker,
  GridColumns,
  PageHeader,
  Spacer,
  Toolbar,
} from "common";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import DateRangeSelector2 from "components/DateRangeSeletor/DateRangeSelector2";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import AutoCompleteFilter from "features/bookings/AutoCompleteFilter";
import useDictionaries from "features/dictionaries/useDictionaries";
import useAccess from "features/profile/useAccess";
import { ProgramItem } from "features/programs/programApi";
import EventCell from "features/schedules/columns/EventCell";
import LimitsCell from "features/schedules/columns/LimitsCell";
import OptionPricesCell from "features/schedules/columns/OptionPricesCell";
import PricesCell from "features/schedules/columns/PricesCell";
import ProgramCell from "features/schedules/columns/ProgramCell";
import I18n from "i18n";
import moment from "moment";
import { ScheduleItem } from "./scheduleApi";
import useScheduleList from "./useScheduleList";
export interface ScheduleListProps extends Omit<DataGridBaseBaseProps, "id"> {
  showHeader?: boolean;
}

export default function ScheduleList({
  showHeader = true,
  ...props
}: ScheduleListProps) {
  const {
    params,
    scope,
    editUrl,
    filter,
    onChange,
    gridFilter,
    showMore,
    setShowMore,
    ...service
  } = useScheduleList();
  const { hasPermissions, canEditItem } = useAccess();
  const { dictionaries } = useDictionaries();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const excelUrl = useExcelUrl("schedules", scope, gridFilter);
  let columns: GridColumns<ScheduleItem> = [
    {
      field: "name",
      headerName: I18n.t("Schedule.attributes.name"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`${editUrl}${params.row?.id}`}
          params={params}
        />
      ),
      flex: 1,
    },
    {
      hide: !!params.boat_id,
      hideable: !params.boat_id,
      field: "boat.name",
      headerName: I18n.t("Schedule.attributes.boat_id"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`/boats/${params.row?.boat_id}`}
          params={params}
        />
      ),
    },
    {
      field: "duration",
      headerName: I18n.t("Schedule.attributes.duration"),
      type: "number",
    },
    {
      field: "date_from",
      headerName: I18n.t("Schedule.attributes.date_from"),
      type: "date",
    },
    {
      field: "date_to",
      headerName: I18n.t("Schedule.attributes.date_to"),
      type: "date",
    },
    {
      field: "program_ids",
      headerName: I18n.t("Schedule.attributes.program_ids"),
      type: "json",
      headerActions: (
        <AutoCompleteFilter<ProgramItem>
          filterKey="programIds"
          filter={filter}
          onChange={nextFilter =>
            onChange({ programIds: nextFilter.programIds || [] })
          }
          options={dictionaries.programs || []}
          title={`${I18n.t("Schedule.attributes.program_ids")} szűrő`}
          label={I18n.t("Schedule.attributes.program_ids")}
          getOptionLabel={program => program.name}
          getOptionValue={program => program.id}
        />
      ),
      renderCell: params => <ProgramCell params={params} />,
    },
    {
      field: "events",
      headerName: I18n.t("Schedule.attributes.events"),
      type: "json",
      renderCell: params => <EventCell params={params} />,
    },
    {
      field: "limits",
      headerName: I18n.t("Schedule.attributes.limits"),
      type: "json",
      renderCell: params => <LimitsCell params={params} />,
    },
    {
      field: "variants",
      headerName: I18n.t("Schedule.attributes.variants"),
      type: "json",
      renderCell: params => <PricesCell params={params} />,
    },
    {
      field: "options",
      headerName: I18n.t("Schedule.attributes.options"),
      type: "json",
      renderCell: params => <OptionPricesCell params={params} />,
    },
  ];

  return (
    <>
      {showHeader && (
        <Box sx={{ px: smDown ? 2 : 0 }}>
          <PageHeader
            avatar={<EditCalendarIcon />}
            title={I18n.t("Schedule.title")}
          >
            {smDown ? (
              <DateRangeSelector2
                isClearable
                numberOfCalendars={1}
                numberOfYears={1}
                value={
                  filter?.range?.start && filter?.range?.end
                    ? {
                        start: moment(filter.range.start),
                        end: moment(filter.range.end),
                      }
                    : undefined
                }
                onChange={(start, end) =>
                  onChange({
                    range:
                      start && end
                        ? {
                            start: start.format("YYYY-MM-DD"),
                            end: end.format("YYYY-MM-DD"),
                          }
                        : undefined,
                  })
                }
                dateButtonMinWidth={150}
              />
            ) : (
              <DateRangePicker
                value={{ from: filter?.range?.start, to: filter?.range?.end }}
                defaultRangeIdStorageKey="schedules_default_range"
                clearBehavior="empty"
                onChange={({ from, to }) =>
                  onChange({
                    range:
                      from && to
                        ? { start: from as any, end: to as any }
                        : undefined,
                  })
                }
              />
            )}
          </PageHeader>
          {!params.boat_id && (
            <BoatCheckboxSelector
              value={filter?.boatIds}
              color={theme.palette.primary.main}
              onChange={evt =>
                onChange({ boatIds: (evt.target.value as any) || [] })
              }
            />
          )}
          <Spacer h={2} />
          <ProgramCheckboxSelector
            value={filter?.programIds}
            onChange={evt =>
              onChange({ programIds: (evt.target.value as any) || [] })
            }
            showMore={showMore}
            setShowMore={setShowMore}
          />
          <Spacer h={2} />
        </Box>
      )}
      <DataGrid
        defaultState={{
          hidden_columns: [
            "variants",
            "options",
            "limits",
            "duration",
            "events",
          ],
          sorting: [{ field: "date_from", direction: "asc" }],
        }}
        {...service}
        columns={columns}
        id="schedules"
        excelUrl={excelUrl}
        filter={gridFilter}
        {...props}
        //getHeaderName={field => I18n.t(`Schedule.attributes.${field.replaceAll(".", "_")}`)}
        editUrl={`${editUrl}:id`}
        renderToolbarLeftItems={({ handleCreate }) => (
          <Toolbar flexWrap="wrap">
            <Checkbox
              label="Lejárt menetrendek"
              value={filter?.includeExpired || false}
              onChange={(evt: any) =>
                onChange({ includeExpired: evt.target.value })
              }
            />
          </Toolbar>
        )}
        canCreate={hasPermissions("schedule.create")}
        canDelete={hasPermissions(["schedule.editany", "schedule.editown"])}
        canEdit={hasPermissions(["schedule.editany", "schedule.editown"])}
        canDeleteRow={params => canEditItem("schedule", params.row)}
        canEditRow={params => canEditItem("schedule", params.row)}
        canBatchActions={hasPermissions([
          "schedule.editany",
          "schedule.editown",
        ])}
        canHandleTrash={hasPermissions([
          "schedule.editany",
          "schedule.editown",
        ])}
      />
    </>
  );
}
